import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchCollectionChat,
  streamCollectionChat,
} from '@/services/data-sources/chat-with-collection';
import {
  CollectionChatMessage,
  CollectionChatRequestPayload,
  CollectionChatSendOptions,
  CollectionChatSource,
  CollectionChatStreamEvent,
} from './types';

const createId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const buildSessionId = () => createId();

const appendParagraph = (existing: string, nextChunk: string) => {
  if (!existing) return nextChunk;
  if (!nextChunk) return existing;
  return `${existing}\n\n${nextChunk}`;
};

interface UseCollectionChatOptions {
  orgId?: string | null;
  collectionId?: string | null;
  token?: string | null;
  includeThoughts?: boolean;
}

interface UseCollectionChatResult {
  messages: CollectionChatMessage[];
  sendMessage: (options: CollectionChatSendOptions) => Promise<void>;
  resetConversation: () => void;
  stop: () => void;
  isStreaming: boolean;
  error: string | null;
  sessionId: string | null;
  addLocalNotice: (text: string) => void;
}

export const useCollectionChat = ({
  orgId,
  collectionId,
  token,
  includeThoughts = true,
}: UseCollectionChatOptions): UseCollectionChatResult => {
  const [messages, setMessages] = useState<CollectionChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const effectiveCollectionId = collectionId || null;
  const effectiveOrgId = orgId || null;

  const cleanupController = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, []);

  const resetConversation = useCallback(() => {
    cleanupController();
    setMessages([]);
    setSessionId(null);
    setIsStreaming(false);
    setError(null);
  }, [cleanupController]);

  useEffect(() => {
    return () => cleanupController();
  }, [cleanupController]);

  useEffect(() => {
    resetConversation();
  }, [effectiveCollectionId, effectiveOrgId, resetConversation]);

  const stop = useCallback(() => {
    if (!isStreaming) return;
    cleanupController();
    setIsStreaming(false);
    setMessages((prev) =>
      prev.map((message) =>
        message.role === 'assistant' && message.status === 'streaming'
          ? {
              ...message,
              status: 'stopped',
              thought: undefined,
              content: message.content || 'Response stopped by user.',
            }
          : message,
      ),
    );
  }, [cleanupController, isStreaming]);

  const handleStreamEvent = useCallback(
    (assistantId: string, event: CollectionChatStreamEvent) => {
      setMessages((prev) =>
        prev.map((message) => {
          if (message.id !== assistantId) {
            return message;
          }

          if (event.type === 'message' && typeof event.data === 'string') {
            return {
              ...message,
              content: appendParagraph(message.content, event.data),
            };
          }

          if (event.type === 'thought' && typeof event.data === 'string') {
            return {
              ...message,
              thought: event.data,
            };
          }

          if (event.type === 'sources' && Array.isArray(event.data)) {
            return {
              ...message,
              sources: event.data as CollectionChatSource[],
            };
          }

          if (
            event.type === 'suggest_chat_mode' &&
            typeof event.data === 'boolean'
          ) {
            return {
              ...message,
              suggestChatMode: event.data,
            };
          }

          if (event.type === 'done') {
            return {
              ...message,
              status: 'complete',
            };
          }

          return message;
        }),
      );
    },
    [],
  );

  const finalizeAssistantMessage = useCallback((assistantId: string) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === assistantId
          ? { ...message, status: 'complete' }
          : message,
      ),
    );
  }, []);

  const markAssistantError = useCallback(
    (assistantId: string, messageText: string) => {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === assistantId
            ? {
                ...message,
                status: 'error',
                error: messageText,
                content: message.content || 'Unable to generate a response.',
              }
            : message,
        ),
      );
    },
    [],
  );

  const sendMessage = useCallback(
    async ({
      message,
      query,
      chatMode,
      scoreThreshold,
      limit,
    }: CollectionChatSendOptions) => {
      if (!message.trim()) {
        return;
      }

      if (!effectiveOrgId || !effectiveCollectionId) {
        setError('Select an organization and collection to start chatting.');
        return;
      }

      if (isStreaming) {
        return;
      }

      const trimmedMessage = message.trim();
      const newSessionId = sessionId ?? buildSessionId();
      const userMessage: CollectionChatMessage = {
        id: createId(),
        role: 'user',
        content: trimmedMessage,
        createdAt: Date.now(),
        status: 'complete',
      };

      const assistantId = createId();
      const assistantMessage: CollectionChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        createdAt: Date.now(),
        status: 'streaming',
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setError(null);
      setIsStreaming(true);

      cleanupController();
      const controller = new AbortController();
      controllerRef.current = controller;

      const payload: CollectionChatRequestPayload = {
        session_id: newSessionId,
        message: trimmedMessage,
        query: query?.trim() || trimmedMessage,
        include_thoughts: includeThoughts,
        stream: true,
        chat_mode: chatMode || 'default',
        score_threshold: chatMode === 'default' ? scoreThreshold : undefined,
        limit:
          chatMode === 'default' || chatMode === 'search' ? limit : undefined,
      };

      try {
        await streamCollectionChat({
          orgId: effectiveOrgId,
          collectionId: effectiveCollectionId,
          token,
          body: payload,
          signal: controller.signal,
          onEvent: (event) => handleStreamEvent(assistantId, event),
        });
        setSessionId(newSessionId);
        finalizeAssistantMessage(assistantId);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Something went wrong.';

        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        } else if (
          errorMessage.toLowerCase().includes('streaming is not supported')
        ) {
          try {
            const fallback = await fetchCollectionChat({
              orgId: effectiveOrgId,
              collectionId: effectiveCollectionId,
              token,
              body: { ...payload, stream: false },
              signal: controller.signal,
            });
            setSessionId(fallback.session_id || newSessionId);

            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? {
                      ...msg,
                      content: fallback.message || '',
                      thought: fallback.thought || undefined,
                      sources: fallback.sources || [],
                      suggestChatMode: fallback.suggest_chat_mode || false,
                      status: 'complete',
                    }
                  : msg,
              ),
            );
          } catch (fallbackError) {
            const fallbackMessage =
              fallbackError instanceof Error
                ? fallbackError.message
                : 'Unable to generate a response.';
            setError(fallbackMessage);
            markAssistantError(assistantId, fallbackMessage);
          }
        } else {
          setError(errorMessage);
          markAssistantError(assistantId, errorMessage);
        }
      } finally {
        setIsStreaming(false);
        controllerRef.current = null;
      }
    },
    [
      cleanupController,
      effectiveCollectionId,
      effectiveOrgId,
      finalizeAssistantMessage,
      handleStreamEvent,
      includeThoughts,
      isStreaming,
      markAssistantError,
      sessionId,
      token,
    ],
  );

  const addLocalNotice = useCallback((text: string) => {
    if (!text.trim()) return;
    const notice: CollectionChatMessage = {
      id: createId(),
      role: 'assistant',
      content: text.trim(),
      createdAt: Date.now(),
      status: 'complete',
    };
    setMessages((prev) => [...prev, notice]);
  }, []);

  return useMemo(
    () => ({
      messages,
      sendMessage,
      resetConversation,
      stop,
      isStreaming,
      error,
      sessionId,
      addLocalNotice,
    }),
    [
      error,
      isStreaming,
      messages,
      resetConversation,
      sendMessage,
      sessionId,
      stop,
      addLocalNotice,
    ],
  );
};
